import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { MdTraffic, MdDateRange, MdSearch } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useLanguage } from '../../context/LanguageContext';

const CustomTooltip = ({ active, payload, label, signal }) => {
    if (active && payload && payload.length) {
        const val = payload[0].value;
        const desc = val === 1 ? signal.state1Description : signal.state0Description;
        const color = val === 1 ? 'var(--success)' : 'var(--danger)';
        return (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 13 }}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }}></div>
                    <span style={{ fontWeight: 700, color: color }}>{desc}</span>
                </div>
            </div>
        );
    }
    return null;
};

const TimelineGraph = ({ logs, initialState, startDate, endDate, signal, language }) => {
    const { t } = useLanguage();
    const fnsLocale = language === 'tr' ? tr : undefined;

    if (!startDate || !endDate) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>{t('companyStatusControl.selectDateRange')}</div>;
    
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    
    if (end <= start) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>{t('companyStatusControl.invalidDateRange')}</div>;

    const chartData = [];
    let currentState = null;

    if (initialState) {
        chartData.push({
            time: format(start, 'yyyy/MM/dd HH:mm', { locale: fnsLocale }),
            timestamp: start,
            value: initialState.value
        });
        currentState = initialState.value;
    } else if (logs.length > 0) {
        chartData.push({
            time: format(start, 'yyyy/MM/dd HH:mm', { locale: fnsLocale }),
            timestamp: start,
            value: 0
        });
        currentState = 0;
    } else {
        chartData.push({
            time: format(start, 'yyyy/MM/dd HH:mm', { locale: fnsLocale }),
            timestamp: start,
            value: 0
        });
        currentState = 0;
    }

    logs.forEach(log => {
        const logTime = new Date(log.timestamp).getTime();
        if (logTime >= start && logTime <= end) {
            chartData.push({
                time: format(logTime, 'yyyy/MM/dd HH:mm', { locale: fnsLocale }),
                timestamp: logTime,
                value: log.value
            });
            currentState = log.value;
        }
    });

    if (chartData.length > 0) {
        chartData.push({
            time: format(end, 'yyyy/MM/dd HH:mm', { locale: fnsLocale }),
            timestamp: end,
            value: currentState
        });
    }

    return (
        <div style={{ height: 200, width: '100%', marginTop: 16 }}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                        <linearGradient id="gradSignal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4f8ef7" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#4f8ef7" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="time" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={30} />
                    <YAxis domain={[0, 1]} ticks={[0, 1]} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip signal={signal} />} />
                    <Area type="stepAfter" dataKey="value" stroke="#4f8ef7" strokeWidth={2.5} fill="url(#gradSignal)"
                        activeDot={{ r: 5, fill: '#4f8ef7', stroke: 'white', strokeWidth: 2 }}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

const SignalCard = ({ signal, startDate, endDate, language }) => {
    const { t } = useLanguage();
    const [logs, setLogs] = useState([]);
    const [initialState, setInitialState] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!startDate || !endDate) return;
        setLoading(true);
        api.get(`/status-signals/${signal._id}/logs`, { params: { startDate, endDate } })
            .then(res => {
                setLogs(res.data.data);
                setInitialState(res.data.initialState);
            })
            .catch(() => toast.error(t('companyStatusControl.signalDataLoadFailed', { name: signal.name })))
            .finally(() => setLoading(false));
    }, [signal._id, startDate, endDate, signal.name, t]);

    const renderCurrentState = () => {
        const val = signal.currentValue;
        if (val === null || val === undefined) {
            return <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('common.noData')}</span>;
        }
        const desc = val === 1 ? signal.state1Description : signal.state0Description;
        const color = val === 1 ? 'var(--success)' : 'var(--danger)';
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }}></div>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{desc}</span>
            </div>
        );
    };

    return (
        <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>{signal.name}</h3>
                    <div style={{ display: 'flex', gap: 16 }}>
                        {renderCurrentState()}
                    </div>
                </div>
            </div>

            {loading ? (
                <div style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="spinner spinner-sm"></span>
                </div>
            ) : (
                <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                        <span>{(new Date(startDate)).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')}</span>
                        <span>{(new Date(endDate)).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')}</span>
                    </div>
                    <TimelineGraph logs={logs} initialState={initialState} startDate={startDate} endDate={endDate} signal={signal} language={language} />
                </div>
            )}
        </div>
    );
};

export default function CompanyStatusControlPage() {
    const { user } = useAuth();
    const { t, language } = useLanguage();
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const [startDateStr, setStartDateStr] = useState(yesterday.toISOString().slice(0, 16));
    const [endDateStr, setEndDateStr] = useState(today.toISOString().slice(0, 16));
    
    const [activeStartDate, setActiveStartDate] = useState(yesterday.toISOString());
    const [activeEndDate, setActiveEndDate] = useState(today.toISOString());

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const sigRes = await api.get(`/status-signals/company/${user.companyId}`);
            setSignals(sigRes.data.data || []);
        } catch (error) {
            toast.error(t('companyStatusControl.signalsLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [user.companyId, t]);

    useEffect(() => {
        if (user?.companyId) {
            fetchData();
        }
    }, [fetchData, user?.companyId]);

    const handleApplyFilter = () => {
        if (!startDateStr || !endDateStr) {
            toast.error(t('companyStatusControl.selectDateRange'));
            return;
        }
        if (new Date(startDateStr) >= new Date(endDateStr)) {
            toast.error(t('companyStatusControl.invalidDateRange'));
            return;
        }
        setActiveStartDate(new Date(startDateStr).toISOString());
        setActiveEndDate(new Date(endDateStr).toISOString());
    };

    if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 48, height: 48 }}></div></div>;

    return (
        <div className="page-wrapper">
            <style>{`
                .signal-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 24px;
                    width: 100%;
                    max-width: 1400px;
                    margin: 0 auto;
                }
                @media (max-width: 1024px) {
                    .signal-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }
                }
            `}</style>
            <div className="page-header" style={{ marginBottom: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16 }}>
                <div>
                    <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                        <MdTraffic style={{ color: 'var(--primary)' }} />
                        {t('companyStatusControl.title')}
                    </h1>
                    <p className="page-subtitle" style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>
                        {t('companyStatusControl.statusControlSubtitle')}
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'var(--bg-card)', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--border)', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <MdDateRange style={{ color: 'var(--text-muted)' }} />
                    <input 
                        type="datetime-local" 
                        className="form-input form-input-sm" 
                        value={startDateStr}
                        onChange={(e) => setStartDateStr(e.target.value)}
                        style={{ width: 'auto' }}
                    />
                    <span style={{ color: 'var(--text-muted)' }}>-</span>
                    <input 
                        type="datetime-local" 
                        className="form-input form-input-sm" 
                        value={endDateStr}
                        onChange={(e) => setEndDateStr(e.target.value)}
                        style={{ width: 'auto' }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={handleApplyFilter} style={{ padding: '0 16px', height: 36 }}>
                        <MdSearch /> {t('common.filter')}
                    </button>
                </div>
            </div>


            <div style={{ width: '100%' }}>
                {signals.length === 0 ? (
                    <div className="empty-state" style={{ padding: '60px 0', maxWidth: 1000, margin: '0 auto' }}>
                        <div className="empty-state-icon" style={{ fontSize: 48 }}>🚥</div>
                        <div className="empty-state-title" style={{ fontSize: 18 }}>{t('companyStatusControl.noSignals')}</div>
                        <div className="empty-state-desc">{t('companyStatusControl.noSignalsAccountDesc')}</div>
                    </div>
                ) : (
                    <div className="signal-grid">
                        {signals.map(signal => (
                            <SignalCard 
                                key={signal._id} 
                                signal={signal} 
                                startDate={activeStartDate} 
                                endDate={activeEndDate} 
                                language={language}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
